/**
 * ContratsChatCFO — Chat portefeuille en langage naturel.
 * Le CFO pose une question → l'IA parse l'intention, filtre le
 * portefeuille, ranke sémantiquement et affiche une table live.
 */
import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Sparkles, Send, Loader2, User, Bot, ArrowRight, MessageSquare,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const fmtEUR = (v) => v == null ? "—" : new Intl.NumberFormat("fr-FR",
  { style: "currency", currency: "EUR", minimumFractionDigits: 0 }).format(v);
const fmtDate = (d) => d ? new Date(d).toLocaleDateString("fr-FR") : "—";

const SUGGESTED = [
  "Quels contrats à indexation ILC signés en 2024 ?",
  "Contrats prestation SaaS > 10 k€ HT/mois",
  "Baux commerciaux avec reconduction tacite qui expirent en 2027",
  "Tous les contrats avec un score de risque > 50",
  "Contrats sans DPA RGPD",
];

export default function ContratsChatCFO() {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef();

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, busy]);

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (q.length < 3) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: q }]);
    setBusy(true);
    try {
      const { data } = await api.post("/contracts/chat", { message: q });
      setMessages((m) => [...m, { role: "assistant", ...data }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", error: apiError(e) }]);
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4 max-w-5xl mx-auto" data-testid="chat-cfo-page">
      <div>
        <div className="text-xs font-medium uppercase tracking-widest text-slate-500">Contrats</div>
        <h1 className="mt-1 text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900"
            style={{ fontFamily: "Manrope, sans-serif" }}>
          Chat portefeuille CFO
        </h1>
        <p className="mt-1 text-sm text-slate-500">
          Pose une question en langage naturel — l'IA parse l'intention,
          filtre ton portefeuille et affiche les résultats en direct.
        </p>
      </div>

      <Card className="h-[calc(100vh-260px)] flex flex-col p-0 overflow-hidden">
        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-50/50">
          {messages.length === 0 && !busy && (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center mb-3">
                <MessageSquare className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-sm font-semibold text-slate-800 mb-1">
                Que veux-tu savoir sur ton portefeuille ?
              </h3>
              <p className="text-xs text-slate-500 max-w-md mb-4">
                Le chat combine filtres structurés + recherche sémantique
                pour te répondre en langage naturel.
              </p>
              <div className="flex flex-wrap gap-1.5 justify-center max-w-2xl">
                {SUGGESTED.map((s) => (
                  <button key={s} onClick={() => send(s)}
                    className="text-xs px-3 py-1.5 rounded-full border border-slate-200 hover:border-blue-400 hover:bg-blue-50 hover:text-blue-700 text-slate-600 transition"
                    data-testid="chat-suggestion">
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((m, i) => (
            <div key={i} className={cn("flex gap-3", m.role === "user" && "flex-row-reverse")}>
              <div className={cn("w-8 h-8 rounded-full flex items-center justify-center shrink-0",
                m.role === "user" ? "bg-slate-200" : "bg-gradient-to-br from-blue-500 to-indigo-600")}>
                {m.role === "user" ? <User className="h-4 w-4 text-slate-700" />
                                      : <Bot className="h-4 w-4 text-white" />}
              </div>
              <div className={cn("flex-1 max-w-[85%]",
                m.role === "user" && "flex justify-end")}>
                {m.role === "user" ? (
                  <div className="bg-blue-600 text-white rounded-2xl rounded-tr-sm px-4 py-2 text-sm inline-block">
                    {m.text}
                  </div>
                ) : m.error ? (
                  <div className="bg-red-50 border border-red-200 text-red-700 rounded-2xl rounded-tl-sm px-4 py-2 text-sm">
                    {m.error}
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="bg-white border border-slate-200 rounded-2xl rounded-tl-sm px-4 py-3">
                      <p className="text-sm text-slate-800 leading-relaxed">{m.summary}</p>
                      {(m.intent?.filters && Object.keys(m.intent.filters).length > 0) && (
                        <div className="mt-2 flex flex-wrap gap-1 text-[10px]">
                          <span className="text-slate-400 uppercase font-medium">Filtres :</span>
                          {Object.entries(m.intent.filters).map(([k, v]) => (
                            <Badge key={k} variant="outline" className="text-[10px]">
                              {k} = {String(v)}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                    {m.count > 0 && (
                      <Card className="p-0 overflow-hidden">
                        <div className="px-3 py-2 border-b bg-slate-50 flex items-center justify-between">
                          <span className="text-xs font-medium text-slate-700">
                            {m.count} contrat(s) · {fmtEUR(m.total_amount_ht)} HT cumulé
                          </span>
                        </div>
                        <div className="max-h-96 overflow-y-auto">
                          <Table>
                            <TableHeader className="sticky top-0 bg-white">
                              <TableRow>
                                <TableHead>Titre</TableHead>
                                <TableHead>Contrepartie</TableHead>
                                <TableHead>Type</TableHead>
                                <TableHead>Signé</TableHead>
                                <TableHead>Fin</TableHead>
                                <TableHead className="text-right">HT/cycle</TableHead>
                                <TableHead>Risque</TableHead>
                                <TableHead />
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {m.results.map((r) => (
                                <TableRow key={r.id} className="hover:bg-blue-50/40 cursor-pointer"
                                          onClick={() => navigate(`/app/contracts/${r.id}`)}
                                          data-testid={`chat-result-${r.id}`}>
                                  <TableCell className="text-xs font-medium">{r.title}</TableCell>
                                  <TableCell className="text-xs text-slate-600">{r.party_name}</TableCell>
                                  <TableCell className="text-[10px] text-slate-500">{r.contract_type}</TableCell>
                                  <TableCell className="text-[11px] font-mono">{fmtDate(r.signature_date)}</TableCell>
                                  <TableCell className="text-[11px] font-mono">{fmtDate(r.end_date)}</TableCell>
                                  <TableCell className="text-xs font-mono text-right">{fmtEUR(r.amount_ht)}</TableCell>
                                  <TableCell>
                                    <Badge className={cn("text-[10px]",
                                      r.risk_score >= 81 ? "bg-red-100 text-red-700"
                                      : r.risk_score >= 51 ? "bg-orange-100 text-orange-700"
                                      : r.risk_score >= 21 ? "bg-amber-100 text-amber-700"
                                      : "bg-emerald-100 text-emerald-700")}>
                                      {r.risk_score}
                                    </Badge>
                                  </TableCell>
                                  <TableCell>
                                    <ArrowRight className="h-3 w-3 text-slate-300" />
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      </Card>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
          {busy && (
            <div className="flex gap-3">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                <Bot className="h-4 w-4 text-white" />
              </div>
              <div className="bg-white border border-slate-200 rounded-2xl rounded-tl-sm px-4 py-2 text-sm text-slate-500 flex items-center gap-2">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> L'IA analyse ton portefeuille…
              </div>
            </div>
          )}
          <div ref={endRef} />
        </div>

        {/* Input */}
        <div className="p-3 border-t bg-white">
          <div className="relative">
            <Input value={input} onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && !busy && send()}
                    placeholder="Pose ta question…"
                    className="pr-12 h-11" disabled={busy}
                    data-testid="chat-input" />
            <Button onClick={() => send()} disabled={busy || input.length < 3}
                    size="sm" className="absolute right-1.5 top-1/2 -translate-y-1/2 h-8 bg-blue-600 hover:bg-blue-700"
                    data-testid="chat-send">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    : <Send className="h-3.5 w-3.5" />}
            </Button>
          </div>
          <div className="text-[10px] text-slate-400 mt-1.5 flex items-center gap-1">
            <Sparkles className="h-2.5 w-2.5" /> Powered by Gemini 2.5 + embeddings sémantiques
          </div>
        </div>
      </Card>
    </div>
  );
}
